import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FullSegment } from 'ts/shared/interfaces/MOResponse';
import {
  getStatus,
  reasons1 as reasons1Const,
  reasons2,
  reasons3,
  reasons4,
} from 'constants/mo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/ui/Button/Button';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import useModalClass from 'hooks/useModalClass';
import ScreenshotSupport from '../../ScreenshotSupport';
import MaximizeModal from '../../MaximizeModal';
import withErrorBoundary from '../../withErrorBoundary';
import { sanitizeString } from 'utils/shared/utils';
import MoSpecialBabyName from './MoSpecialBabyName';
import { createPortal } from 'react-dom';
import useDraggableModal from 'hooks/useDraggableModal';

interface SpecialRequest {
  code: string;
  data: any;
}

interface MoSpecialRequestModalProps {
  segment?: FullSegment;
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  onSubmit: (message: string) => void;
  book?: any;
  paxes?: any;
  setSpecialRequestList?: (message: SpecialRequest[]) => void;
  specialRequestList?: SpecialRequest[];
}

const MoSpecialRequestModal: FC<MoSpecialRequestModalProps> = ({
  segment,
  openModal,
  setOpenModal,
  ...props
}) => {
  const [t] = useTranslation();
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const classList = useModalClass(openModal);
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);
  const [message, setMessage] = useState('');
  const [reasons1, setReasons1] = useState(reasons1Const);
  const [reasons2State, setReasons2State] = useState(reasons2);
  const [reasons3State, setReasons3State] = useState(reasons3);
  const [reasons4State, setReasons4State] = useState(reasons4);
  const [showBabyNameModal, setShowBabyNameModal] = useState(false);
  const openBaby = useRef('');

  useEffect(() => {
    if (openModal && props.specialRequestList) {
      setMessage(
        props.specialRequestList
          .map((reason) => {
            const translated = t(reason.code, reason.data) as string;
            return translated.replace(/<[^>]*>?/g, '');
          })
          .join('\n')
      );

      const newReasons1 = reasons1.map((reason) => ({
        ...reason,
        checked: props.specialRequestList?.some((req) => req.code === reason.key) ?? false,
      }));

      setReasons1(newReasons1);

      const newReasons2 = reasons2.map((reason) => ({
        ...reason,
        checked: props.specialRequestList?.some((req) => req.code === reason.key) ?? false,
      }));

      setReasons2State(newReasons2);

      const newReasons3 = reasons3.map((reason) => ({
        ...reason,
        checked: props.specialRequestList?.some((req) => req.code === reason.key) ?? false,
      }));

      setReasons3State(newReasons3);

      const newReasons4 = reasons4.map((reason) => ({
        ...reason,
        checked: props.specialRequestList?.some((req) => req.code === reason.key) ?? false,
      }));

      setReasons4State(newReasons4);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModal]);

  const onClickCheckbox = (checked: boolean, reason: string) => {
    if ((reason === 'mo.confirm_baby_cot' || reason === 'mo.add_confirm_baby_cot') && checked) {
      setShowBabyNameModal(true);
      openBaby.current = reason;
      return;
    }

    const reasonData = [...reasons3, ...reasons4].find((r) => r.key === reason)?.data;

    if (checked) {
      const newSpecialRequestList = [
        ...(props.specialRequestList ?? []),
        { code: reason, data: reasonData || {} },
      ];

      props.setSpecialRequestList?.(newSpecialRequestList);

      const newMessage = newSpecialRequestList
        .map((req) => {
          const translated = t(req.code, req.data) as string;
          return translated.replace(/<[^>]*>?/g, '');
        })
        .join('\n');
      setMessage(newMessage);
    } else {
      const newSpecialRequestList =
        props.specialRequestList?.filter((r) => r.code !== reason) ?? [];

      const newMessage = newSpecialRequestList
        .map((req) => {
          const translated = t(req.code, req.data) as string;
          return translated.replace(/<[^>]*>?/g, '');
        })
        .join('\n');
      setMessage(newMessage);

      props.setSpecialRequestList?.(newSpecialRequestList);
    }
  };

  const onSubmitBaby = (model: any) => {
    const newRecord = {
      code: openBaby.current,
      data: { fullName: model.fullName ? model.fullName : '____', dob: model.dob },
    };
    const newSpecialRequestList = [...(props.specialRequestList ?? []), newRecord];

    props.setSpecialRequestList?.(newSpecialRequestList);

    if (newSpecialRequestList) {
      const updatedMessage = newSpecialRequestList
        .map((req) => {
          const translated = t(req.code, req.data) as string;
          return translated.replace(/<[^>]*>?/g, '');
        })
        .join('\n');
      setMessage(updatedMessage);
    }
  };

  return createPortal(
    <div
      className="modal-overlay modal-overlay-top modal-maximizable" style={{ direction: 'ltr' }}
      ref={modalOverlayRef}
      onClick={(e) => e.target === e.currentTarget && setOpenModal(false)}
    >
      <div ref={modalRef} className={classList} style={{ ...modalStyle, width: '80%' }}>
        <div
          className={'modal-title  draggable-modal-handle' + (isDragging ? ' dragging' : '')}
          {...dragHandleProps}
        >
          <h5 className="title">
            {t('mo.change_request')}{' '}
            {segment && (
              <Fragment>
                {' '}-{' '}Order: {segment.orderId}{' '}
                Segment:
                {segment.orderSegId}{' '}
                <span className={`mo-status ${getStatus(segment.roomsStatusCode)}`}>
                  {segment.roomsStatusCode}
                </span>
              </Fragment>
            )}
          </h5>
          <div data-no-drag="true">
            <ScreenshotSupport />
            <MaximizeModal modalOverlayRef={modalOverlayRef} />
            <FontAwesomeIcon icon={faTimes} onClick={() => setOpenModal(false)} />
          </div>
        </div>

        <div className="modal-body text-sm modal-body-overflow">
          <div className="row checkbox-end">
            <div className="mb-3 col-md-6 col-12 text-lh">
              <div className="title">{t('mo.room_preferences')}</div>
              {reasons1.map((reason, index) => (
                <Checkbox
                  className="mb-5"
                  key={index}
                  checked={reason.checked}
                  onChange={(val) => {
                    const newReasons1 = [...reasons1];
                    newReasons1[index].checked = val;
                    setReasons1(newReasons1);
                    onClickCheckbox(val, reason.key);
                  }}
                  htmlLabel={<span dangerouslySetInnerHTML={{ __html: t(reason.key) }}></span>}
                />
              ))}
            </div>
            <div className="mb-3 col-md-6 col-12 text-lh">
              <div className="title">{t('mo.guest_notes')}</div>
              {reasons2State.map((reason, index) => (
                <Checkbox
                  key={index}
                  className="mb-5"
                  checked={reason.checked}
                  onChange={(val) => {
                    const newReasons2 = [...reasons2State];
                    newReasons2[index].checked = val;
                    setReasons2State(newReasons2);
                    onClickCheckbox(val, reason.key);
                  }}
                  htmlLabel={<span dangerouslySetInnerHTML={{ __html: t(reason.key) }}></span>}
                />
              ))}
              <div className="title mt-20">{t('mo.additional_requirements')}</div>
              <div className="mb-5 text-bold color-danger">{t('mo.if_child')}</div>
              {reasons3State.map((reason, index) => (
                <Checkbox
                  key={index}
                  className="mb-5"
                  checked={reason.checked}
                  onChange={(val) => {
                    const newReasons3 = [...reasons3State];
                    newReasons3[index].checked = val;
                    setReasons3State(newReasons3);
                    onClickCheckbox(val, reason.key);
                  }}
                  htmlLabel={
                    <span dangerouslySetInnerHTML={{ __html: t(reason.key, reason.data) }}></span>
                  }
                />
              ))}
              <div className="mb-5 text-bold color-danger">{t('mo.if_no_child')}</div>

              {reasons4State.map((reason, index) => (
                <Checkbox
                  key={index}
                  className="mb-5"
                  checked={reason.checked}
                  onChange={(val) => {
                    const newReasons4 = [...reasons4State];
                    newReasons4[index].checked = val;
                    setReasons4State(newReasons4);
                    onClickCheckbox(val, reason.key);
                  }}
                  htmlLabel={
                    <span dangerouslySetInnerHTML={{ __html: t(reason.key, reason.data) }}></span>
                  }
                />
              ))}
            </div>
          </div>
          <div className="mb-5 mt-30 text-xsm">{t('mo.choose_requests_can_edit')}</div>
          <textarea
            className="form-control text-sm"
            onChange={(e) => setMessage(sanitizeString(e.target.value))}
            style={{ height: '150px', resize: 'none' }}
            value={message}
          />
        </div>

        <div className="modal-footer">
          <Button
            title={t('save')}
            className="btn btn-primary ml-0 mr-a"
            onClick={() => props.onSubmit(message)}
          />
          <Button
            title={t('cancel')}
            className="btn btn-default"
            onClick={() => setOpenModal(false)}
          />
        </div>
      </div>
      {showBabyNameModal && (
        <MoSpecialBabyName onClose={() => setShowBabyNameModal(false)} onSubmit={onSubmitBaby} />
      )}
    </div>
    , document.body);
};

export default withErrorBoundary(MoSpecialRequestModal, 'MoSpecialRequestModal');
